import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isFunction } from 'lodash';
import { Subject } from 'rxjs';
import { v4 } from 'uuid';
import { AppStore } from '../../../../store/app.store';
import { BrowserManagerDataService } from '../../../browser-manager-data/browser-manager-data.service';
import { LocalizationService } from '../../../localization/localization.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { supportUrlsLocalized, URLS } from '../../constants/urls.constant';
import { ISticky } from '../../directives/sticky/sticky.directive';
import { IIcon } from '../icon/icon.interface';
import { IconSizesEnum } from '../icon/icons-sizes.enum';
import { GlobalBannerTypesEnum } from './global-banner.enum';
import { IGlobalBanner } from './global-banner.interface';
import { GlobalBannerService } from './global-banner.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-global-banner',
  templateUrl: 'global-banner.component.html',
  styleUrls: ['global-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalBannerComponent implements OnInit, OnDestroy {
  @ViewChild('globalBanner', { static: true }) globalBanner: ElementRef;
  @ViewChild('globalBannerMessage', { static: false })
  globalBannerMessage: ElementRef;

  private _id: string;
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _onlineBannerId: string;
  private _persistent: boolean;
  private _onDestroy: (
    bannerId: string,
    component: GlobalBannerComponent
  ) => void;
  private _onDismiss: (
    bannerId: string,
    component: GlobalBannerComponent
  ) => void;
  private _onAction: (
    bannerId: string,
    component: GlobalBannerComponent
  ) => void;

  isVisible: boolean;
  actionIcon: IIcon;
  dismissible: boolean;
  type: GlobalBannerTypesEnum;
  message: string;
  containsHtml: boolean;
  trackingObject: ITrackingObject;
  trackingAction: ITrackingAction;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingAdditionalProperties: any;
  stickyConfig: ISticky;
  e2eId: string;

  constructor(
    private _globalBannerService: GlobalBannerService,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private _browserManagerDataService: BrowserManagerDataService,
    private _translateService: TranslateService,
    private _appStore: AppStore,
    private _elementRef: ElementRef,
    private _localizationService: LocalizationService
  ) {}

  ngOnInit() {
    this._onlineBannerId = v4();

    this._appStore.globalBanner$
      .pipe(takeUntil(this._destroyed))
      .subscribe((banner: IGlobalBanner) => {
        if (banner) {
          const trackingScreenName = banner.trackingScreenName;

          this._id = banner.id;
          this._persistent = !!banner.persistent;
          this._onDestroy = banner.onDestroy;
          this._onDismiss = banner.onDismiss;
          this._onAction = banner.onAction;

          this.isVisible = true;
          this.message = banner.message;
          this.type = banner.type;
          this.containsHtml = banner.containsHtml;
          this.dismissible = banner.dismissible;
          this.trackingObject = banner.trackingObject;
          this.trackingAction = banner.trackingAction;
          this.e2eId = banner.e2eId;
          this.trackingAdditionalProperties = {
            target: banner.bannerDestination,
          };

          if (trackingScreenName) {
            if (!this.trackingAction) {
              this.trackingAction = this._trackingActionBuilder.buildTapped(
                trackingScreenName
              );
            }

            if (this.trackingObject) {
              this._trackingDispatcher.trackEvent(
                this.trackingObject,
                this._trackingActionBuilder.buildPresented(trackingScreenName)
              );
            }
          }

          if (this._onAction) {
            this.actionIcon = {
              name: 'chevron-right',
              size: IconSizesEnum.medium,
            };
          } else if (this.dismissible) {
            this.actionIcon = {
              name: 'close-navigation',
              size: IconSizesEnum.medium,
            };
          } else {
            this.actionIcon = null;
          }

          setTimeout(() => {
            if (banner.isSticky) {
              this.stickyConfig = { scrollEl: '.app-page-wrapper' };

              this._changeDetectorRef.markForCheck();
            }

            if (isFunction(banner.onShow)) {
              banner.onShow(this._id, this);
            }
          }, 100);
        } else {
          this._id = null;

          this.stickyConfig = null;
          this.isVisible = false;
        }

        this._changeDetectorRef.markForCheck();
      });

    this._browserManagerDataService.online$
      .pipe(takeUntil(this._destroyed))
      .subscribe((online) => {
        if (!online) {
          const supportUrl = supportUrlsLocalized(
            URLS.SUPPORT,
            this._localizationService.getLocale()
          );

          this._globalBannerService.showBanner({
            type: GlobalBannerTypesEnum.error,
            message: this._translateService.instant(
              'ERROR.INTERNET_DISCONNECTED',
              {
                url: supportUrl,
              }
            ),
            containsHtml: true,
            dismissible: true,
            isSticky: true,
            id: this._onlineBannerId,
          });
        } else {
          this._globalBannerService.hideBanner(this._onlineBannerId);
        }
      });
  }

  onBannerAction(evt: Event, stopPropagation: boolean) {
    if (stopPropagation) {
      evt.stopPropagation();
    }

    if (isFunction(this._onAction)) {
      this._onAction(this._id, this);
    }

    if (this.dismissible) {
      this._globalBannerService.hideBanner();

      if (isFunction(this._onDismiss)) {
        this._onDismiss(this._id, this);
      }
    }
  }

  ngOnDestroy() {
    if (this.isVisible && !this._persistent) {
      this._globalBannerService.hideBanner();
    }

    if (isFunction(this._onDestroy)) {
      this._onDestroy(this._id, this);
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
